import { useEffect, useState } from 'react';

import {
  Accordion,
  Box,
  Button,
  ButtonGroup,
  FormField,
  Heading,
  Icon,
  Input,
  Label,
  Modal,
  Paragraph,
  Textarea,
  TextButton,
  Toast,
  ToggleCard,
  useToast,
} from '@zillow/constellation-10';
import { IconLocationFilled, IconMapFilled } from '@zillow/constellation-icons';

import { useRentalManagerUserSupportWebApiHandler_submitRequest } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../../selectors/useSelector';
import CorrectMapPinCSEvents from '../../../model/ClickstreamEvent/CorrectMapPinCSEvents';
import propertyUtils from '../../../utils/propertyUtils';
import { RentalManagerSupportHelpTopic } from '@zg-rentals/ts-rental-manager-api-beta-client';

export const SUCCESS_TOAST_MESSAGE = 'Your location adjustment request has been successfully submitted.';

const MapPinAdjustmentRequestModal = ({ isFromPostingPath = false }: { isFromPostingPath?: boolean }) => {
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const { mutate: submitRequest } = useRentalManagerUserSupportWebApiHandler_submitRequest();

  const [locationDescription, setLocationDescription] = useState('');
  const [activeId, setActiveId] = useState(-1);

  const { user, email, street, unit, city, state, zip, listingAlias, currentProperty } = useSelector((state) => ({
    email: state.user.email,
    user: state.user,
    street: state.properties.currentProperty.street,
    unit: state.properties.currentProperty.unit,
    city: state.properties.currentProperty.city,
    state: state.properties.currentProperty.state,
    zip: state.properties.currentProperty.zip,
    listingAlias: state.properties.currentProperty.listingAlias,
    currentProperty: state.properties.currentProperty,
  }));

  useEffect(() => {
    if (isFromPostingPath) {
      CorrectMapPinCSEvents.ViewMapPinAdjustmentModalFromPostingPath({ user });
    }
  }, []);

  const renderToggleCard = (id: number, icon: React.ReactNode, title: string, description: string) => (
    <ToggleCard
      selected={activeId === id}
      onClick={() => handleToggleCardClick(id)}
      css={{ textAlign: 'center', padding: '{spacing.600} {spacing.300}', flex: 1 }}
      data-testid={`MapPinAdjustmentRequestModal-ToggleCard-${id}`}
    >
      <Icon size="md" css={{ marginBottom: '200' }}>
        {icon}
      </Icon>
      <Paragraph textStyle="body-sm-bold">{title}</Paragraph>
      <Paragraph textStyle="body-xs" color="text.subtle">
        {description}
      </Paragraph>
    </ToggleCard>
  );

  const handleToggleCardClick = (id: number) => {
    setActiveId(id);
    setLocationDescription('');
  };

  const handleSubmit = () => {
    CorrectMapPinCSEvents.ClickSubmitMapPinAdjustmentRequest({
      user,
      currentProperty,
      triggerLocation: isFromPostingPath ? 'posting_path' : 'zrm_property_overview',
    });
    submitRequest(
      {
        postData: {
          helpTopicKey: RentalManagerSupportHelpTopic.FIX_MAP_PIN,
          helpTopicString: 'Fix map pin',
          description: locationDescription,
          email,
          preferredEmail: '',
          listingAddressFreeForm: propertyUtils.formatAddress({ street, unit, city, state, zip })
            .formattedCompleteAddress,
          aliases: listingAlias ? [listingAlias] : [],
          userTypeKey: '',
          userTypeString: '',
        },
      },
      {
        onSuccess: () => {
          // invalidate queries to refresh the alert display
          queryClient.invalidateQueries({ queryKey: ['getPropertyDetailsOverview', listingAlias] });
          queryClient.invalidateQueries({ queryKey: ['listingDetails', listingAlias] });
          enqueueToast(<Toast tone="success">{SUCCESS_TOAST_MESSAGE}</Toast>);
        },
        onError: (err: unknown) => {
          enqueueToast(
            <Toast tone="critical">
              {DatarouterError.getDisplayMessage(err) || 'An error has occurred while submitting'}
            </Toast>,
          );
        },
      },
    );
  };

  return (
    <Modal
      size="md"
      header={
        <Heading
          level={1}
          data-eventid={CorrectMapPinCSEvents.ViewMapPinAdjustmentModalFromPostingPath.data_event_id}
          data-eventdescription="View modal event"
        >
          Submit a location adjustment request
        </Heading>
      }
      body={
        <>
          <Heading level={4} css={{ marginBottom: '600' }}>
            Select a verification method
          </Heading>
          <Paragraph>Select how you’d like to provide the correct location information</Paragraph>
          <Box
            css={{
              display: 'flex',
              gap: '600',
              justifyContent: 'center',
              alignItems: 'stretch',
              marginBlock: '400',
              marginInline: '900',
            }}
          >
            {renderToggleCard(0, <IconMapFilled />, 'Google Maps link', 'Share a link from Google Maps')}
            {renderToggleCard(1, <IconLocationFilled />, 'Latitude/Longitude', 'Provide exact numerical coordinates')}
          </Box>
          {activeId === 0 && (
            <FormField
              control={
                <Textarea
                  value={locationDescription}
                  placeholder="Paste the Google Maps link to your property here..."
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setLocationDescription(e.target.value)}
                />
              }
              label={<Label required={true}>Google Maps link</Label>}
              required={true}
            />
          )}
          {activeId === 1 && (
            <>
              <FormField
                control={
                  <Input
                    value={locationDescription}
                    placeholder="e.g. 34.0522° N, 118.2437° W"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocationDescription(e.target.value)}
                  />
                }
                label={<Label required={true}>Coordinates</Label>}
                required={true}
              />
              <Accordion.Root>
                <Accordion.Item value="MapPinAdjustmentRequestModal-HowToFindCoordinates" css={{ marginTop: '400' }}>
                  <Accordion.Header>
                    <Paragraph>How to find your coordinates </Paragraph>
                  </Accordion.Header>
                  <Accordion.Panel>
                    {/* eslint-disable-next-line constellation/voice-and-tone */}
                    <strong>Google Maps: </strong>Right-click your property location, select “What’s here?”, and copy
                    the coordinates from the popup.
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion.Root>
            </>
          )}
          <Paragraph textStyle="body-xs-bold" css={{ marginTop: '300', marginBottom: '100' }}>
            When can I expect a response?
          </Paragraph>
          <Paragraph textStyle="body-xs">
            We respond to requests during weekday business hours, <br />
            Monday - Friday from 8am PST - 4pm PST.
          </Paragraph>
        </>
      }
      dividers={true}
      footer={
        <ButtonGroup aria-label="modal actions">
          <Modal.Close>
            <TextButton>Cancel</TextButton>
          </Modal.Close>
          <Modal.Close>
            <Button
              emphasis="filled"
              tone="brand"
              onClick={handleSubmit}
              disabled={!locationDescription}
              data-eventid={CorrectMapPinCSEvents.ClickSubmitMapPinAdjustmentRequest.data_event_id}
            >
              Submit
            </Button>
          </Modal.Close>
        </ButtonGroup>
      }
      trigger={null}
      multiModalTriggerId="MapPinAdjustmentRequestModal"
    />
  );
};

export default MapPinAdjustmentRequestModal;
